import { useEffect, useMemo, useRef, useState } from 'react';
import {
  calculatePayPeriod, calculateSalary,
  type Competence, type IrrfDeductionMode, type OvertimeInput,
} from '@amigodoclt/core';
import { Icon } from '../components/Icon.js';
import { NumberField, NumericInput, SelectField, Segmented, Stepper, ToggleRow } from '../components/Field.js';
import { ResultCard } from '../components/ResultCard.js';
import { amountOf, money, sumOf } from '../lib/format.js';
import { deductionPresets, overtimePresets } from '../lib/calculators.js';
import { defaultSalaryForm, salaryInputOf, useSalaryForm, type SalaryForm } from '../lib/salary-form.js';
import { useReveal } from '../lib/motion.js';

const newId = () => crypto.randomUUID();

/** No mobile o protótipo tira o seletor de periodicidade de dentro da barra fixa. */
function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 760px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 760px)');
    const update = () => setNarrow(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}

export function Salary() {
  const root = useRef<HTMLDivElement>(null);
  const narrow = useNarrow();
  useReveal(root);

  const [form, setForm] = useSalaryForm();
  const {
    competence, base, dependents, pension, hours, nightHours, nightPremium, bonusType, bonusValue,
    vtEnabled, vtPercent, irrfMode, period, advancePercent, overtimes, deductions,
  } = form;
  const set = <K extends keyof SalaryForm>(key: K) => (value: SalaryForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const input = useMemo(() => salaryInputOf(form), [form]);

  const result = useMemo(
    () => (period === 'biweekly'
      ? { biweekly: true as const, data: calculatePayPeriod({ ...input, advancePercent }) }
      : { biweekly: false as const, data: calculateSalary(input) }),
    [advancePercent, input, period],
  );

  const addOvertime = (preset?: { label: string; premiumPercent: number }) =>
    set('overtimes')([...overtimes, { id: newId(), label: preset?.label ?? 'Hora extra', hours: 0, premiumPercent: preset?.premiumPercent ?? 50, dsrPercent: 0 }]);
  const updateOvertime = (rowId: string, field: keyof OvertimeInput, value: string | number) =>
    set('overtimes')(overtimes.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
  const addDeduction = (label = 'Desconto personalizado') =>
    set('deductions')([...deductions, { id: newId(), label, amount: 0 }]);

  return (
    <main className="page" ref={root}>
      <div className="page-head" data-reveal>
        <div className="page-head-copy">
          <p className="eyebrow">Estimativa {competence}</p>
          <h1 className="page-title">Salário líquido</h1>
          <p className="lead">Escolha bônus, descontos e condições do pagamento para enxergar cada rubrica.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-secondary" onClick={() => window.print()}><Icon name="print" size={18} />Imprimir</button>
          <button type="button" className="btn-secondary" onClick={() => setForm(defaultSalaryForm)}><Icon name="restart_alt" size={18} />Limpar</button>
        </div>
      </div>

      <div className="sticky-bar glass-nav" aria-label="Resumo do salário">
        <div className="bar-cell filled with-icon">
          <Icon name="edit" size={narrow ? 13 : 22} />
          <div>
            <span>Salário-base</span>
            <NumericInput label="Salário-base mensal" value={base} onChange={set('base')} />
          </div>
        </div>
        <div className="bar-cell muted">
          <span>Descontos</span>
          <strong>−{money(sumOf(result.data.deductions))}</strong>
        </div>
        <div className="bar-cell fgts">
          <span>FGTS (não sai)</span>
          <strong>{money(amountOf(result.data.employerContributions, 'FGTS (depósito)'))}</strong>
        </div>
        <div className="bar-cell net">
          <div>
            <span>{result.biweekly ? '2ª parcela' : 'Líquido'}</span>
            <strong>{money(result.biweekly ? result.data.secondInstallment : result.data.net)}</strong>
          </div>
          <Icon name="payments" size={26} fill />
        </div>
        {narrow ? null : (
          <div className="bar-segmented">
            <Segmented
              label="Periodicidade" value={period} onChange={set('period')} variant="bar"
              options={[{ value: 'monthly', label: 'Mensal', icon: 'calendar_month' }, { value: 'biweekly', label: 'Quinzenal', icon: 'event_repeat' }]}
            />
          </div>
        )}
      </div>

      {narrow ? (
        <Segmented
          label="Periodicidade" value={period} onChange={set('period')}
          options={[{ value: 'monthly', label: 'Mensal', icon: 'calendar_month' }, { value: 'biweekly', label: 'Quinzenal', icon: 'event_repeat' }]}
        />
      ) : null}

      <div className="page-layout">
        <form className="glass-form glass" onSubmit={(event) => event.preventDefault()}>
          <section className="form-section" data-reveal>
            <h2 className="section-title"><span className="section-icon"><Icon name="account_balance_wallet" size={20} /></span>Base e pagamento</h2>
            <div className="field-grid">
              <SelectField
                label="Competência" value={String(competence)}
                onChange={(value) => set('competence')(Number(value) as Competence)}
                options={[{ value: '2026', label: '2026' }, { value: '2025', label: '2025' }]}
              />
              <NumberField label="Salário-base mensal" value={base} onChange={set('base')} />
            </div>
            <div className="field-grid">
              <NumberField label="Horas mensais" value={hours} onChange={set('hours')} min={1} step={1} suffix="h" />
              <Stepper label="Dependentes" value={dependents} onChange={set('dependents')} min={0} step={1} />
            </div>
            {period === 'biweekly'
              ? <NumberField label="Adiantamento da 1ª quinzena" value={advancePercent} onChange={set('advancePercent')} max={100} suffix="%" />
              : null}
          </section>

          <section className="form-section" data-reveal>
            <h2 className="section-title"><span className="section-icon"><Icon name="star" size={20} /></span>Bônus</h2>
            <p className="helper"><Icon name="info" size={16} />Entra no salário bruto e nas bases de INSS, IRRF e FGTS.</p>
            <div className="field-grid">
              <span className="field-label">
                Tipo
                <Segmented
                  label="Tipo de bônus" value={bonusType} onChange={set('bonusType')} variant="box"
                  options={[{ value: 'fixed', label: 'Valor fixo' }, { value: 'percent', label: '% do salário' }]}
                />
              </span>
              <NumberField
                label={bonusType === 'fixed' ? 'Bônus (R$)' : 'Bônus (%)'}
                value={bonusValue} onChange={set('bonusValue')}
                max={bonusType === 'percent' ? 1000 : undefined}
                suffix={bonusType === 'fixed' ? 'R$' : '%'}
              />
            </div>
          </section>

          <section className="form-section" data-reveal>
            <h2 className="section-title"><span className="section-icon"><Icon name="account_balance" size={20} /></span>Descontos e IR</h2>
            <div className="field-grid">
              <NumberField label="Pensão alimentícia" value={pension} onChange={set('pension')} />
              <SelectField
                label="Método de dedução no IR" value={irrfMode}
                onChange={(value) => set('irrfMode')(value as IrrfDeductionMode)}
                options={[
                  { value: 'auto', label: 'Mais vantajoso automaticamente' },
                  { value: 'simplified', label: 'Desconto simplificado' },
                  { value: 'legal', label: 'Deduções legais' },
                ]}
              />
            </div>
            <ToggleRow label="Usar vale-transporte" icon="directions_bus" value={vtEnabled} onChange={set('vtEnabled')} />
            {vtEnabled
              ? <NumberField label="Vale-transporte (máx. 6% do salário-base)" value={vtPercent} onChange={(value) => set('vtPercent')(Math.min(6, value))} max={6} suffix="%" />
              : null}
          </section>

          <section className="form-section" data-reveal>
            <h2 className="section-title"><span className="section-icon"><Icon name="more_time" size={20} /></span>Horas extras</h2>
            <div className="chips">
              {overtimePresets.map((preset) => (
                <button type="button" className="chip" key={preset.label} onClick={() => addOvertime(preset)}>
                  <Icon name="add" size={16} />{preset.label}
                </button>
              ))}
              <button type="button" className="chip dashed" onClick={() => addOvertime()}><Icon name="edit" size={16} />Personalizada</button>
            </div>
            {overtimes.map((row) => (
              <div className="dyn-row overtime" key={row.id}>
                <label className="field-label">Rubrica
                  <input className="field" value={row.label ?? ''} onChange={(event) => updateOvertime(row.id, 'label', event.target.value)} />
                </label>
                <label className="field-label">Horas
                  <NumericInput value={row.hours} onChange={(value) => updateOvertime(row.id, 'hours', value)} />
                </label>
                <label className="field-label">Adicional %
                  <NumericInput value={row.premiumPercent ?? 50} onChange={(value) => updateOvertime(row.id, 'premiumPercent', value)} />
                </label>
                <label className="field-label">DSR %
                  <NumericInput value={row.dsrPercent ?? 0} onChange={(value) => updateOvertime(row.id, 'dsrPercent', value)} />
                </label>
                <button type="button" className="row-remove" aria-label={`Remover ${row.label ?? 'hora extra'}`} onClick={() => set('overtimes')(overtimes.filter((item) => item.id !== row.id))}>
                  <Icon name="delete" size={20} />
                </button>
              </div>
            ))}
            <div className="field-grid">
              <NumberField label="Horas noturnas" value={nightHours} onChange={set('nightHours')} min={0} suffix="h" />
              <NumberField label="Adicional noturno" value={nightPremium} onChange={set('nightPremium')} min={0} suffix="%" />
            </div>
          </section>

          <section className="form-section" data-reveal>
            <h2 className="section-title"><span className="section-icon"><Icon name="receipt_long" size={20} /></span>Descontos personalizados</h2>
            <div className="chips">
              {deductionPresets.map((preset) => (
                <button type="button" className="chip" key={preset.label} onClick={() => addDeduction(preset.label)}>
                  <Icon name={preset.icon} size={16} />{preset.label}
                </button>
              ))}
            </div>
            {deductions.map((row) => (
              <div className="dyn-row deduction" key={row.id}>
                <label className="field-label">Nome
                  <input className="field" value={row.label} onChange={(event) => set('deductions')(deductions.map((item) => (item.id === row.id ? { ...item, label: event.target.value } : item)))} />
                </label>
                <label className="field-label">Valor (R$)
                  <NumericInput value={row.amount} onChange={(value) => set('deductions')(deductions.map((item) => (item.id === row.id ? { ...item, amount: value } : item)))} />
                </label>
                <button type="button" className="row-remove" aria-label={`Remover ${row.label}`} onClick={() => set('deductions')(deductions.filter((item) => item.id !== row.id))}>
                  <Icon name="delete" size={20} />
                </button>
              </div>
            ))}
          </section>
        </form>

        <ResultCard
          data={result.data}
          subtitle={`${result.biweekly ? 'Quinzenal' : 'Mensal'} · ${competence}`}
          className="result-sticky"
          installments={result.biweekly ? [
            { label: '1ª quinzena', detail: `Adiantamento de ${result.data.advancePercent}% do salário-base`, amount: result.data.advance },
            { label: '2ª quinzena', detail: 'Líquido do mês menos o adiantamento', amount: result.data.secondInstallment },
          ] : undefined}
        />
      </div>
    </main>
  );
}
